import type { Evaluation } from '@compass/client/types/evaluation';
import { ArrowDownLeft, ArrowLeftRight, ArrowUpRight } from 'lucide-react';
import React, { useMemo } from 'react';

import { FormattedDateTime } from '@/components/FormattedDateTime';
import { GenericTable } from '@/components/GenericTable';
import { Badge } from '@/components/ui/badge';
import { QcScoreBadge } from '@/pages/qc/components/QcScoreBadge';
import { getEvaluationDirection } from '@/pages/qc/utils';
import type { TableColumnConfig, TableConfig } from '@/types/table';
import { cn } from '@/utils';
import { buildOrgRoutePath } from '@/utils/routes';

import { ViewAllEvaluationsButton } from './ViewAllEvaluationsButton';

interface ProgramOption {
  label: string;
  value: string;
}

interface AgentRecentEvaluationsTableProps {
  organizationId: string;
  evaluations: Evaluation[];
  loading?: boolean;
  error?: unknown;
  programOptions: ProgramOption[];
  viewAllHref: string;
}

const renderTextCell = (value?: string | null, className?: string) =>
  value ? (
    <span className={className}>{value}</span>
  ) : (
    <span className="text-muted-foreground">--</span>
  );

const BooleanFlagBadge: React.FC<{
  value?: boolean;
  trueLabel: string;
  falseLabel: string;
  trueClassName: string;
}> = ({ value, trueLabel, falseLabel, trueClassName }) => {
  if (value == null) return <span className="text-muted-foreground">--</span>;

  return (
    <Badge
      variant="outline"
      className={cn(
        'justify-center border-transparent',
        value ? trueClassName : 'bg-positive text-positive-foreground',
      )}
    >
      {value ? trueLabel : falseLabel}
    </Badge>
  );
};

const DirectionBadge: React.FC<{ direction: 'Inbound' | 'Outbound' | 'Internal' | null }> = ({
  direction,
}) => {
  if (!direction) return <span className="text-muted-foreground">--</span>;

  if (direction === 'Inbound') {
    return (
      <Badge variant="outline" className="gap-1 border-transparent text-foreground">
        <ArrowDownLeft className="h-4 w-4 text-blue-700" /> {direction}
      </Badge>
    );
  }

  if (direction === 'Outbound') {
    return (
      <Badge variant="outline" className="gap-1 border-transparent text-foreground">
        <ArrowUpRight className="h-4 w-4 text-purple-700" /> {direction}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="gap-1 border-transparent text-foreground">
      <ArrowLeftRight className="h-4 w-4 text-slate-700" /> {direction}
    </Badge>
  );
};

export const AgentRecentEvaluationsTable: React.FC<AgentRecentEvaluationsTableProps> = ({
  organizationId,
  evaluations,
  loading,
  error,
  programOptions,
  viewAllHref,
}) => {
  const programNameById = useMemo(
    () => new Map(programOptions.map(option => [option.value, option.label])),
    [programOptions],
  );

  const columns: TableColumnConfig<Evaluation>[] = useMemo(
    () => [
      {
        key: 'evaluation_id',
        header: 'Eval ID',
        width: 260,
        accessor: item => renderTextCell(item.id, 'block max-w-[240px] truncate font-mono text-xs'),
        copyable: true,
        copyValue: item => item.id || '',
      },
      {
        key: 'created_at',
        header: 'Call Start Time',
        width: 160,
        // Displays call_start_time (AQ-512) — the axis this table is windowed/sorted by
        // (useAgentRecentEvaluations filters + sorts on call_start_time). Falls back to
        // created_at / evaluated_at for rows without a call start.
        accessor: item => (
          <FormattedDateTime value={item.call_start_time || item.created_at || item.evaluated_at} />
        ),
      },
      {
        key: 'program',
        header: 'Program',
        width: 220,
        accessor: item =>
          renderTextCell(
            item.program_id ? programNameById.get(item.program_id) || item.program_id : null,
            'block max-w-[200px] truncate',
          ),
      },
      {
        key: 'direction',
        header: 'Direction',
        width: 150,
        accessor: item => <DirectionBadge direction={getEvaluationDirection(item)} />,
      },
      {
        key: 'aggregate_score',
        header: 'Score',
        width: 120,
        accessor: item => <QcScoreBadge score={item.aggregate_score} />,
      },
      {
        key: 'compliance',
        header: 'Compliance',
        width: 140,
        accessor: item => (
          <BooleanFlagBadge
            value={item.flags?.compliance_failed}
            trueLabel="Fail"
            falseLabel="Pass"
            trueClassName="bg-critical text-critical-foreground"
          />
        ),
      },
      {
        key: 'escalation',
        header: 'Escalation',
        width: 140,
        accessor: item => (
          <BooleanFlagBadge
            value={item.flags?.escalation}
            trueLabel="Yes"
            falseLabel="No"
            trueClassName="bg-attention text-attention-foreground"
          />
        ),
      },
    ],
    [programNameById],
  );

  const config: TableConfig<Evaluation> = useMemo(
    () => ({
      columns,
      getRowLink: item =>
        item.id && organizationId ? buildOrgRoutePath(`/qc/${item.id}`, organizationId) : '',
      getRowKey: item => item.id || item.call_id || `${item.program_id}-${item.created_at}`,
      emptyState: {
        title: 'No recent evaluated calls',
        description: 'There are no evaluated calls for this agent in the selected filters.',
      },
    }),
    [columns, organizationId],
  );

  return (
    <section className="space-y-3">
      <header className="flex items-center justify-between gap-3">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold leading-5 text-foreground">Recent Calls</h3>
          <p className="text-xs leading-5 text-muted-foreground">
            Last 10 calls for the selected date range and filters.
          </p>
        </div>
        <ViewAllEvaluationsButton href={viewAllHref} size="xs" />
      </header>
      <GenericTable<Evaluation>
        config={config}
        data={evaluations}
        loading={loading}
        error={
          error
            ? error instanceof Error
              ? error.message
              : 'Could not load recent evaluated calls.'
            : null
        }
        currentPage={1}
        hasNextPage={false}
        onPageChange={() => undefined}
        hidePagination
      />
    </section>
  );
};
